/**
 * @fileoverview Shared Attachment schema used by both frontend and backend.
 * Polymorphic file uploads linked to any entity via entity_type + entity_id.
 */

import { z } from 'zod';
import { accountTrackingFields, commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { ID_PATTERNS } from './types.js';

/**
 * Attachment validation schema
 */
export const AttachmentSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.ATTACHMENT, 'ID must be in attachment nanoid7 format (at + digit 2-9 + 4 alphanumeric)'),
  entity_type: z.string()
    .min(1, 'Entity type is required')
    .max(50, 'Entity type must be at most 50 characters')
    .regex(/^[a-z][a-z_]*$/, 'Entity type must be lowercase snake_case (e.g. note, project, person)'),
  entity_id: z.string()
    .min(1, 'Entity ID is required')
    .max(7, 'Entity ID must be at most 7 characters'),
  filename: commonValidators.requiredString('Filename', 255),
  mime_type: commonValidators.optionalString(100),
  file_size_bytes: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? null : Number(val)),
    z.number().int('File size must be an integer').positive('File size must be positive').nullable()
  ).optional(),
  storage_path: z.string().min(1, 'Storage path is required'),
  thumbnail_path: z.string().optional().nullable(),
  metadata: commonValidators.jsonField('Metadata'),
  ...accountTrackingFields,
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(AttachmentSchema);

export const AttachmentCreateSchema = CreateSchema;
export const AttachmentUpdateSchema = UpdateSchema;

export type Attachment = z.infer<typeof AttachmentSchema>;
export type AttachmentCreate = z.infer<typeof AttachmentCreateSchema>;
export type AttachmentUpdate = z.infer<typeof AttachmentUpdateSchema>;

export const ATTACHMENT_FIELDS = getSchemaFields(AttachmentSchema);
